import { useRef, useState } from 'react'
import { Link } from 'react-router'
import { CircleAlert, Clock, Rocket, Search, Send } from 'lucide-react'
import { RequestFailed } from '../api/client'
import { useContacts } from '../api/contacts'
import {
  useCampaignAction,
  useCampaignRecipients,
  useSendCampaignTest,
  type Campaign,
  type CampaignHold,
  type CampaignProgress,
  type RecipientStatus,
} from '../api/campaigns'
import { failureReasonText, fullTime, newId } from '../lib/format'
import { Button } from '@/components/ui/button'
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { cn } from '@/lib/utils'

const currencySign: Record<string, string> = { INR: '₹' }
const n = (x: number) => x.toLocaleString('en-IN')

function money(amount: string, currency: string | undefined): string {
  return `${currency ? (currencySign[currency] ?? `${currency} `) : ''}${amount}`
}

// The launch confirmation: who gets it, what it costs, how it fits the
// daily limit, and when. Every number comes from the server.
export function LaunchDialog({ campaign, disabled }: { campaign: Campaign; disabled: boolean }) {
  const launch = useCampaignAction(campaign.id, 'launch')
  const e = campaign.estimate
  const scheduled = campaign.sendAt !== undefined
  const error = launch.error as RequestFailed | null
  if (!e) return null
  const room = e.dailyLimit !== undefined ? Math.max(0, e.dailyLimit - (e.usedToday ?? 0)) : undefined
  const unpriced = e.recipients - e.priced

  return (
    <Dialog onOpenChange={() => launch.reset()}>
      <DialogTrigger asChild>
        <Button type="button" disabled={disabled || e.recipients === 0}>
          {scheduled ? <Clock aria-hidden="true" /> : <Rocket aria-hidden="true" />}
          {scheduled ? 'Schedule' : 'Send now'}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{scheduled ? `Schedule ${campaign.name}?` : `Send ${campaign.name} now?`}</DialogTitle>
          <DialogDescription>
            {n(e.recipients)} contact{e.recipients === 1 ? '' : 's'} will get it, each as a {e.category} template message.
          </DialogDescription>
        </DialogHeader>
        <ul className="flex flex-col gap-2 text-sm">
          <li>
            {e.rate && e.amount && e.priced > 0
              ? `About ${money(e.amount, e.currency)}: ${n(e.priced)} × ${money(String(Number(e.rate)), e.currency)} per delivered message. Meta bills you directly, for delivered messages only.`
              : `No price on file for ${e.category} messages. Meta bills its own rate for each delivered message.`}
            {unpriced > 0 && e.priced > 0 && ` ${n(unpriced)} with numbers outside India are not in this figure; Meta bills its own rate for them.`}
          </li>
          {room !== undefined && (
            <li>
              {e.recipients <= room
                ? `Fits in your daily limit: ${n(e.usedToday ?? 0)} of ${n(e.dailyLimit ?? 0)} customers messaged first in the last 24 hours.`
                : room === 0
                  ? `Your daily limit of ${n(e.dailyLimit ?? 0)} new customers is used up for now. It starts by itself as the 24-hour limit frees up.`
                  : `Your daily limit lets ${n(room)} more customers get a first message now. The other ${n(e.recipients - room)} follow by themselves as the 24-hour limit frees up.`}
            </li>
          )}
          {scheduled && campaign.sendAt && (
            <li>It goes out {fullTime(campaign.sendAt)}. Until then you can move it back to draft.</li>
          )}
          <li className="text-muted-foreground">Opt-in and do-not-message are checked again right before each message is sent.</li>
        </ul>
        {error && <p role="alert" className="text-sm text-destructive">{error.message}</p>}
        <DialogFooter>
          <DialogClose asChild>
            <Button type="button" variant="outline">Not yet</Button>
          </DialogClose>
          <Button type="button" disabled={launch.isPending} onClick={() => launch.mutate()}>
            {launch.isPending ? 'Launching' : scheduled ? 'Schedule' : `Send to ${n(e.recipients)}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// Sends the campaign's message to one opted-in contact, as a test.
export function TestDialog({ campaign, disabled }: { campaign: Campaign; disabled: boolean }) {
  const [q, setQ] = useState('')
  const [chosen, setChosen] = useState<string | undefined>(undefined)
  const contacts = useContacts({ q, optIn: 'optedIn', suppressed: false })
  const send = useSendCampaignTest(campaign.id)
  // One key per chosen contact; a retry after a failure reuses it.
  const key = useRef(newId())
  const items = contacts.data?.pages[0]?.items ?? []
  const picked = items.find((c) => c.id === chosen)
  const error = send.error as RequestFailed | null

  const reset = () => {
    setChosen(undefined)
    setQ('')
    send.reset()
    key.current = newId()
  }

  return (
    <Dialog onOpenChange={reset}>
      <DialogTrigger asChild>
        <Button type="button" variant="outline" disabled={disabled}>
          <Send aria-hidden="true" />
          Send a test
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Send a test</DialogTitle>
          <DialogDescription>
            One contact gets this campaign's message now, with their name filled in. It is a real message: Meta bills it and it counts toward the daily limit.
          </DialogDescription>
        </DialogHeader>
        {send.isSuccess ? (
          <p role="status" className="text-sm text-primary-text">
            Test queued for {picked?.name ?? 'the contact'}.{' '}
            <Link to={`/inbox/${send.data.conversationId}`} className="font-semibold underline underline-offset-2">See it in the inbox</Link>
          </p>
        ) : (
          <>
            <label className="flex items-center gap-2 rounded-md border border-input bg-card px-3">
              <Search className="size-4 text-muted-foreground" aria-hidden="true" />
              <span className="sr-only">Search opted-in contacts</span>
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name or number" className="border-0 px-0 shadow-none focus-visible:ring-0" />
            </label>
            <div className="max-h-64 overflow-y-auto rounded-lg border">
              {contacts.isPending ? (
                <div role="status" aria-label="Loading contacts" className="flex flex-col gap-2 p-2">
                  <Skeleton className="h-10 w-full" />
                  <Skeleton className="h-10 w-full" />
                </div>
              ) : contacts.isError ? (
                <p role="alert" className="p-3 text-sm text-destructive">{(contacts.error as RequestFailed).message}</p>
              ) : items.length === 0 ? (
                <p className="p-3 text-sm text-muted-foreground">No opted-in contacts match. Only contacts who agreed to receive messages can get a test.</p>
              ) : (
                <ul role="listbox" aria-label="Opted-in contacts" className="divide-y">
                  {items.map((c) => (
                    <li key={c.id} role="option" aria-selected={chosen === c.id}>
                      <button
                        type="button"
                        onClick={() => {
                          setChosen(c.id)
                          key.current = newId()
                          send.reset()
                        }}
                        className={cn('flex min-h-11 w-full flex-col px-3 py-2 text-left text-sm hover:bg-accent', chosen === c.id && 'bg-primary-soft hover:bg-primary-soft')}
                      >
                        <span className="font-semibold">{c.name}</span>
                        {c.waId && <span className="text-xs text-muted-foreground">+{c.waId}</span>}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </>
        )}
        {error && <p role="alert" className="text-sm text-destructive">{error.message}</p>}
        <DialogFooter>
          <DialogClose asChild>
            <Button type="button" variant="outline">{send.isSuccess ? 'Done' : 'Close'}</Button>
          </DialogClose>
          {!send.isSuccess && (
            <Button
              type="button"
              disabled={!chosen || send.isPending}
              onClick={() => chosen && send.mutate({ contactId: chosen, clientMessageId: key.current })}
            >
              {send.isPending ? 'Sending' : picked ? `Send to ${picked.name}` : 'Send test'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

const holdText: Record<CampaignHold, string> = {
  dailyLimit: 'Paused for the daily limit on new customers. It continues by itself',
  templateUnavailable: 'Paused: the template is no longer approved by Meta. It continues when Meta approves it again, or cancel it.',
  notConnected: 'Paused: the number cannot send. It continues once an owner reconnects it.',
}

// Where a launched campaign stands. Each recipient is counted once, by how
// far their message got.
export function ProgressPanel({ campaign }: { campaign: Campaign }) {
  const p: CampaignProgress | undefined = campaign.progress
  const status =
    campaign.status === 'scheduled'
      ? campaign.sendAt
        ? `Scheduled for ${fullTime(campaign.sendAt)}.`
        : 'Scheduled.'
      : campaign.status === 'sending' && !campaign.startedAt
        ? 'Preparing the list of recipients.'
        : campaign.status === 'sending'
          ? 'Sending.'
          : campaign.status === 'done' && campaign.finishedAt
            ? `Every message was queued by ${fullTime(campaign.finishedAt)}. Sent, delivered and read keep counting up as WhatsApp reports them.`
            : campaign.status === 'cancelled' && campaign.cancelledAt
              ? `Cancelled ${fullTime(campaign.cancelledAt)}. Messages queued before that still went out.`
              : ''

  const reached = p ? p.sent + p.delivered + p.read : 0
  const rows: { label: string; value: number; tone?: 'danger' | 'muted' }[] = p
    ? [
        { label: 'Sent', value: reached },
        { label: 'Delivered', value: p.delivered + p.read },
        { label: 'Read', value: p.read },
        { label: 'Failed', value: p.failed, tone: 'danger' },
        { label: 'Queued', value: p.queued, tone: 'muted' },
        { label: 'Waiting', value: p.waiting, tone: 'muted' },
        { label: 'Skipped', value: p.skipped, tone: 'muted' },
        ...(p.cancelled > 0 ? [{ label: 'Not sent, cancelled', value: p.cancelled, tone: 'muted' as const }] : []),
      ]
    : []
  const toSend = p ? p.recipients - p.skipped : 0

  return (
    <section aria-labelledby="progress-h" aria-live="polite" className="flex flex-col gap-3 rounded-xl border bg-card p-4">
      <h2 id="progress-h" className="text-sm font-semibold">Progress</h2>
      <p className="text-sm">{status}</p>
      {campaign.hold && (
        <p className="flex gap-2 rounded-lg bg-attention-soft px-3 py-2 text-sm text-attention-text">
          <CircleAlert className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
          <span>
            {holdText[campaign.hold]}
            {campaign.hold === 'dailyLimit' && (campaign.waitingUntil ? ` around ${fullTime(campaign.waitingUntil)}.` : '.')}
          </span>
        </p>
      )}
      {p && p.recipients > 0 && (
        <>
          <div
            className="flex h-2 overflow-hidden rounded-full bg-muted"
            role="img"
            aria-label={`${n(reached)} of ${n(toSend)} sent, ${n(p.failed)} failed`}
          >
            <span className="bg-primary" style={{ width: `${(100 * (p.delivered + p.read)) / Math.max(1, toSend)}%` }} />
            <span className="bg-primary/50" style={{ width: `${(100 * p.sent) / Math.max(1, toSend)}%` }} />
            <span className="bg-destructive" style={{ width: `${(100 * p.failed) / Math.max(1, toSend)}%` }} />
          </div>
          <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm sm:grid-cols-3 lg:grid-cols-2">
            {rows.map((r) => (
              <div key={r.label} className="flex items-baseline justify-between gap-2">
                <dt className={cn('text-muted-foreground', r.tone === 'danger' && r.value > 0 && 'text-destructive')}>{r.label}</dt>
                <dd className="font-semibold tabular-nums">{n(r.value)}</dd>
              </div>
            ))}
          </dl>
        </>
      )}
      {campaign.launchedAt && (
        <p className="text-xs text-muted-foreground">
          Launched {fullTime(campaign.launchedAt)}
          {campaign.launchedByName ? ` by ${campaign.launchedByName}` : ''}.
        </p>
      )}
    </section>
  )
}

// Unschedule and cancel, for owners and admins.
export function RunActions({ campaign }: { campaign: Campaign }) {
  const unschedule = useCampaignAction(campaign.id, 'unschedule')
  const cancel = useCampaignAction(campaign.id, 'cancel')
  const error = (unschedule.error ?? cancel.error) as RequestFailed | null
  if (campaign.status !== 'scheduled' && campaign.status !== 'sending') return null
  const waiting = campaign.progress ? campaign.progress.waiting : undefined

  return (
    <div className="flex flex-col gap-2">
      {error && <p role="alert" className="text-sm text-destructive">{error.message}</p>}
      {campaign.status === 'scheduled' && (
        <Button type="button" variant="outline" disabled={unschedule.isPending} onClick={() => unschedule.mutate()}>
          {unschedule.isPending ? 'Moving back' : 'Move back to draft'}
        </Button>
      )}
      <Dialog onOpenChange={() => cancel.reset()}>
        <DialogTrigger asChild>
          <Button type="button" variant="ghost" className="text-destructive">Cancel campaign</Button>
        </DialogTrigger>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancel {campaign.name}?</DialogTitle>
            <DialogDescription>
              {campaign.status === 'scheduled'
                ? 'Nothing has been sent yet, and nothing will be. A cancelled campaign cannot be restarted.'
                : `${waiting !== undefined ? `The ${n(waiting)} still waiting` : 'Recipients still waiting'} will not get it. Messages already queued still go out. A cancelled campaign cannot be restarted.`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="outline">Keep it</Button>
            </DialogClose>
            <Button type="button" variant="destructive" disabled={cancel.isPending} onClick={() => cancel.mutate()}>
              {cancel.isPending ? 'Cancelling' : 'Cancel campaign'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

const skipText = {
  notOptedIn: 'has not agreed to receive messages',
  doNotMessage: 'is on do-not-message',
  noPhone: 'has no phone number on file',
} as const

// The failed and skipped recipients, and why: what a team acts on.
export function RecipientList({ campaign }: { campaign: Campaign }) {
  const p = campaign.progress
  const tabs = ([['failed', p?.failed ?? 0], ['skipped', p?.skipped ?? 0]] as const).filter(([, count]) => count > 0)
  const [tab, setTab] = useState<RecipientStatus | undefined>(undefined)
  const current = tab ?? tabs[0]?.[0]
  const list = useCampaignRecipients(campaign.id, current ?? 'failed', current !== undefined)
  if (tabs.length === 0 || !current) return null
  const items = list.data?.pages.flatMap((pg) => pg.items) ?? []

  return (
    <section aria-labelledby="recipients-h" className="flex flex-col gap-3 rounded-xl border bg-card p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 id="recipients-h" className="font-semibold">Who did not get it</h2>
        <Tabs value={current} onValueChange={(v) => setTab(v as RecipientStatus)}>
          <TabsList>
            {tabs.map(([s, count]) => (
              <TabsTrigger key={s} value={s} className="capitalize">{s} {n(count)}</TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      </div>
      {list.isPending ? (
        <div role="status" aria-label="Loading recipients" className="flex flex-col gap-2">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      ) : list.isError ? (
        <p role="alert" className="text-sm text-destructive">{(list.error as RequestFailed).message}</p>
      ) : (
        <>
          <ul className="divide-y">
            {items.map((r) => (
              <li key={r.contactId} className="flex flex-col gap-0.5 py-2 text-sm">
                <span className="flex flex-wrap items-baseline gap-x-2">
                  <Link to={`/contacts/${r.contactId}`} className="font-semibold hover:underline">{r.name}</Link>
                  {r.waId && <span className="text-xs text-muted-foreground">+{r.waId}</span>}
                </span>
                <span className="text-muted-foreground">
                  {r.skipReason
                    ? `Skipped: ${skipText[r.skipReason]}.`
                    : r.failureReason
                      ? `Not delivered: ${failureReasonText[r.failureReason]}.`
                      : `Not delivered${r.errorTitle ? `: ${r.errorTitle}` : ''}.`}
                </span>
              </li>
            ))}
          </ul>
          {list.hasNextPage && (
            <Button variant="outline" className="self-center" disabled={list.isFetchingNextPage} onClick={() => void list.fetchNextPage()}>
              {list.isFetchingNextPage ? 'Loading' : 'Show more'}
            </Button>
          )}
        </>
      )}
    </section>
  )
}
